/**
 * Internationalization - i18n
 *
 * Based on the ECMAScript Internationalization API
 *
 * @see {@link http://www.ecma-international.org/ecma-402/1.0/}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl}
 *
 * @return {object}
 */

export default {
	currencyCode: 'CHF',
	locale: 'de-CH',

	/**
	 * Initialize
	 *
	 * @return {object} this object
	 */
	init: function() {
		var locale = this.getLocale(),
			currency = this.getCurrency();

		this.numberFormatter = new window.Intl.NumberFormat(locale);
		this.currencyFormatter = new window.Intl.NumberFormat(locale, { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
		this.dateFormatter = new window.Intl.DateTimeFormat(locale);

		return this;
	},

	/**
	 * Get the current locale code
	 *
	 * @return {string} locale
	 */
	getLocale: function() {
		return this.locale;
	},

	/**
	 * Get the current currency code
	 *
	 * @return {string} currency
	 */
	getCurrency: function() {
		return this.currencyCode;
	},

	/**
	 * Format a date object to a locale string
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat}
	 *
	 * @param  {date} date
	 * @param  {object} options
	 * @return {string} locale formatted number
	 */
	formatDate: function(date, options) {
		return typeof options === 'object' ? new window.Intl.DateTimeFormat(this.getLocale(), options).format(date) : this.dateFormatter.format(date);
	},

	/**
	 * Format a date object to a locale time string
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat}
	 *
	 * @param  {date} date
	 * @return {string} locale formatted number
	 */
	formatTime: function(date) {
		return this.formatDate(date, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
	},

	/**
	 * Format a number to a locale string
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat}
	 *
	 * @param  {number} number
	 * @return {string} locale formatted number
	 */
	formatNumber: function(number, options) {
		return typeof options === 'object' ? new window.Intl.NumberFormat(this.getLocale(), options).format(number) : this.numberFormatter.format(number);
	},

	formatPercentage: function(number, decimals) {
		var decimalAmount = typeof decimals === 'number' ? decimals : 2;

		return this.formatNumber(number, { style: 'percent', minimumFractionDigits: decimalAmount, maximumFractionDigits: decimalAmount });
	},

	formatNumberAsCurrency: function(number, currency, decimalPlaces = 2) {
		return this.formatNumber(number, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces }) + ' ' + currency;
	},

	formatNumberAsDecimal: function(number, decimals) {
		var decimalAmount = typeof decimals === 'number' ? decimals : 2;

		return this.formatNumber(number, { minimumFractionDigits: decimalAmount, maximumFractionDigits: decimalAmount });
	},

	/**
	 * Format a number to a locale currency string
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat}
	 *
	 * @param  {number} number
	 * @param  {string|boolean} [currency] ISO 4217 currency code, e.g. USD, JPY or false
	 * @param  {object} options
	 * @return {string}
	 */
	formatCurrency: function(number, currency, options) {
		var value;

		if (currency && typeof currency === 'string') {
			value = new window.Intl.NumberFormat(this.getLocale(), { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
		} else if (currency === false) {
			value = this.formatNumber(number, options);
		} else {
			value = this.currencyFormatter.format(number);
		}

		return value;
	},

	/**
	 * Get the decimal separator
	 *
	 * @return {string}
	 */
	getDecimalSeparator: function() {
		return this.formatNumber(1, { minimumFractionDigits: 1 }).charAt(1);
	},

	/**
	 * Get the thousand separator
	 *
	 * When the separator is a number (the first zero in '1000'), there isn't a separator for that locale defined
	 *
	 * @return {string}
	 */
	getThousandSeparator: function() {
		var separator = this.formatNumber(1000).charAt(1);

		return isNaN(parseInt(separator, 10)) ? separator : '';
	},

	getMonths: function(locale) {
		locale = locale || this.getLocale();

		var months = {
			'de-DE': ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
			'fr-FR': ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
			'nl-NL': ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
			'pl-PL': ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień']
		};

		return months[locale];
	},

	getShortMonths: function(locale) {
		locale = locale || this.getLocale();

		var shortMonths = {
			'de-DE': ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
			'fr-FR': ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'],
			'nl-NL': ['Jan.', 'Feb.', 'Mrt.', 'Apr.', 'Mei', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dec.'],
			'pl-PL': ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru']
		};

		return shortMonths[locale];
	},

	getDays: function(locale) {
		locale = locale || this.getLocale();

		var days = {
			'de-DE': ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
			'fr-FR': ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
			'nl-NL': ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
			'pl-PL': ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota']
		};

		return days[locale];
	},

	getShortDays: function(locale) {
		locale = locale || this.getLocale();

		var shortDays = {
			'de-DE': ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'],
			'fr-FR': ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'],
			'nl-NL': ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
			'pl-PL': ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.']
		};

		return shortDays[locale];
	}

};
